import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { 
  Search, Eye, Trash2, CheckCircle, Clock, XCircle, 
  Loader2, Mail, MessageSquare, Calendar
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import "../styles/AdminQuote.css"; 

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_inquiries")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) toast.error("Could not sync inquiries");
    else setInquiries(data || []);
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    const toastId = toast.loading(`Marking as ${newStatus}...`);
    
    const { error } = await supabase
      .from("contact_inquiries")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      toast.error("Update failed", { id: toastId });
    } else {
      toast.success(`Inquiry ${newStatus}`, { id: toastId });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
      setSelectedInquiry(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    const { error } = await supabase.from("contact_inquiries").delete().eq("id", id);
    if (!error) {
      setInquiries(prev => prev.filter(i => i.id !== id));
      toast.success("Inquiry removed");
    }
  };

  const filteredData = inquiries.filter(i => {
    const matchesSearch = i.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          i.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const status = i.status || 'New';
    
    if (statusFilter === "Active") return matchesSearch && (status === "New" || status === "Pending");
    if (statusFilter === "All") return matchesSearch;
    return matchesSearch && status === statusFilter;
  });

  return (
    <div className="rni-admin-container">
      <Toaster position="top-center" containerStyle={{ zIndex: 99999 }} />

      <header className="rni-header">
        <div className="header-left">
          <h1>General Inquiries</h1>
          <p>Manage messages from the contact page</p>
        </div>
      </header>

      <div className="rni-controls">
        <div className="search-wrapper">
          <Search size={18} />
          <input 
            placeholder="Search by sender..." 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>

        <div className="tabs">
          {["Active", "Resolved", "All"].map(t => (
            <button 
              key={t} 
              className={statusFilter === t ? "tab active" : "tab"}
              onClick={() => setStatusFilter(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="table-card">
        <table className="rni-main-table">
          <thead>
            <tr>
              <th>Sender</th>
              <th>Message Snippet</th>
              <th>Date</th>
              <th>Status</th>
              <th style={{textAlign: 'right'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="loading-state"><Loader2 className="rni-spin" /></td></tr>
            ) : filteredData.length > 0 ? (
              filteredData.map(i => (
                <tr key={i.id}>
                  <td className="entity-cell">
                    <strong>{i.full_name}</strong>
                    <span>{i.email}</span>
                  </td>
                  <td>
                    <span className="type-pill" style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'}}>
                      {i.message}
                    </span>
                  </td>
                  <td>{new Date(i.created_at).toLocaleDateString()}</td>
                  <td><span className={`badge ${i.status?.toLowerCase() || 'new'}`}>{i.status || 'New'}</span></td>
                  <td style={{textAlign: 'right'}}>
                    <div className="row-actions">
                      <button onClick={() => setSelectedInquiry(i)} className="view-btn">
                        <Eye size={14} /> View
                      </button>
                      <button onClick={() => handleDelete(i.id)} className="del-btn"><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="5" style={{textAlign: 'center', padding: '40px', color: '#94a3b8'}}>No messages found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selectedInquiry && (
          <div className="glass-overlay" onClick={() => setSelectedInquiry(null)}>
            <motion.div 
              className="premium-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close-btn" onClick={() => setSelectedInquiry(null)}>
                <XCircle size={24} />
              </button>

              <div className="modal-header-info">
                <span className="modal-subtitle">Communication Log</span>
                <h2>{selectedInquiry.full_name}</h2>
              </div>

              <div className="modal-grid">
                <div className="grid-item">
                  <label><Mail size={12}/> Email</label>
                  <p>{selectedInquiry.email}</p>
                </div>
                <div className="grid-item">
                  <label><Calendar size={12}/> Received</label>
                  <p>{new Date(selectedInquiry.created_at).toLocaleString()}</p>
                </div>
                <div className="grid-item">
                  <label><Clock size={12}/> Current Status</label>
                  <p>{selectedInquiry.status || "New"}</p>
                </div>
              </div>

              <div className="modal-desc">
                <label><MessageSquare size={12}/> Full Message</label>
                <div className="desc-inner">
                  {selectedInquiry.message}
                </div>
              </div>

              <div className="modal-footer-btns">
                <button className="action-b approve" onClick={() => handleStatusChange(selectedInquiry.id, 'Resolved')}>
                  <CheckCircle size={18}/> Mark Resolved
                </button>
                <button className="action-b pending" onClick={() => handleStatusChange(selectedInquiry.id, 'Pending')}>
                  <Clock size={18}/> Set Pending
                </button>
                <button className="action-b decline" onClick={() => handleDelete(selectedInquiry.id)}>
                  <Trash2 size={18}/> Delete Entry
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminInquiries;